import { AddInvoiceDialoge } from '../components/invoices/addInvoiceDialog';
import InvoicePageHeader from '../components/invoices/invoicePageHeader';
import InvoicesFiltration from '../components/invoices/invoiceFiltration';
import InvoicesListView from '../components/invoices/invoicesListView';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Divider, Box, LinearProgress } from '@mui/material';
import { useHasPermission } from '../hooks/useHasPermission';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';

const Invoices = () => {

    const hasInvoiceCreatePermission = useHasPermission('facture_create');

    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [totalInvoices, setTotalInvoices] = useState('');
    const [invoicesFetched, setInvoicesFetched] = useState(0);
    const [openAddInvoice, setOpenAddInvoice] = useState(false);
    const [filteredInvoicesCount, setFilteredInvoicesCount] = useState(0);
    const [invoiceFilters, setInvoicefilters] = useState(
        JSON.parse(localStorage.getItem('invoiceFilters')) ||
        { debouncedSearch: '', amount: null, company: '', date: null }
    );
    const defaultSortBy = {
        date: null,
        invoice_number: null,
        company: null,
        amount: null,
    };

    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('invoiceSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });

    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'invoices',
        pathname: '/dashboard/factures',
        displayMode: 'list',
        itemSizes: { list: 70 },
        resetOnFilters: invoiceFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(invoiceFilters);

    useEffect(() => {
        localStorage.setItem('invoiceFilters', JSON.stringify(invoiceFilters));
    }, [invoiceFilters]);

    useEffect(() => {
        localStorage.setItem('invoiceSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    const fetchInvoices = useCallback(async () => {
        try {
            setLoading(true)
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('invoice', { params: { offset: adjustedOffset, limit, filters: invoiceFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedInvoices = [];
                // Update the fetched records in their correct positions
                response?.data?.invoicesData?.forEach((file, index) => { updatedInvoices[adjustedOffset + index] = file });
                // Update the state with the new files array
                setLoading(false);
                setInvoices(updatedInvoices);
                setFilteredInvoicesCount(response?.data?.filteredinvoicesCount);
                setTotalInvoices(response?.data?.totalInvoicesCount);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching invoices:', error);
            CustomErrorLogger(error, 'fetch invoices in invoices page');
        }
    }, [offset, invoiceFilters, sortBy]);

    const fetchInvoicesFiltrationArrays = useCallback(async () => {
        try {
            const response = await axios.get('invoice/getInvoiceFiltrationArrays', { params: { invoiceFilters } });
            if (response.status === 200 || response.status === 201) {
                setCompanies(response?.data?.companyArray);
            }
        } catch (error) {
            console.error('Error Fetching invoices filtration arrays:', error);
            CustomErrorLogger(error, 'fetch invoices filtration arrays in invoices page');
        }
    }, [invoiceFilters]);

    useEffect(() => {
        fetchInvoicesFiltrationArrays()
    }, [fetchInvoicesFiltrationArrays]);

    useEffect(() => {
        fetchInvoices()
    }, [fetchInvoices]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Factures" list={true}>
            <InvoicePageHeader setOpenAddInvoice={setOpenAddInvoice} />
            <Divider />
            {loading ?
                <Box>
                    <InvoicesFiltration
                        filtersEmpty={filtersEmpty}
                        invoiceFilters={invoiceFilters}
                        setInvoicefilters={setInvoicefilters}
                        totalInvoices={totalInvoices}
                        invoicesFetched={invoicesFetched}
                        filteredInvoicesCount={filteredInvoicesCount}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                filtersEmpty && invoices?.length === 0 ?
                    <NoRecord
                        description="Aucun facture ajouté pour le moment"
                        buttonTitle="Ajouter une facture"
                        handleOpenDialog={() => setOpenAddInvoice(true)}
                        hasPermission={hasInvoiceCreatePermission}
                    /> :
                    <Box>
                        <InvoicesFiltration
                            filtersEmpty={filtersEmpty}
                            invoiceFilters={invoiceFilters}
                            setInvoicefilters={setInvoicefilters}
                            totalInvoices={totalInvoices}
                            invoicesFetched={invoicesFetched}
                            filteredInvoicesCount={filteredInvoicesCount}
                        />
                        <InvoicesListView
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                            scrollRef={scrollRef}
                            invoices={invoices}
                            setOffset={setOffset}
                            companies={companies}
                            invoiceFilters={invoiceFilters}
                            setInvoicefilters={setInvoicefilters}
                            setInvoicesFetched={setInvoicesFetched}
                            filteredInvoicesCount={filteredInvoicesCount}
                        />
                    </Box>
            }

            {openAddInvoice &&
                <AddInvoiceDialoge
                    openAddInvoice={openAddInvoice}
                    setOpenAddInvoice={setOpenAddInvoice}
                />
            }
        </Page>
        </ListNavigationProvider>
    );
};

export default Invoices;